// An IZ exam (the questions behind a course `test` unit) as a paintable quiz body.
// Pure, like fromBank.ts: the loader fetches and sanitizes, this only maps.

import { convertURL } from '../../api/cdn.ts';
import type { IzQuestion } from '../../question-bank/izteach.ts';
import { attachGroupChildren, toViewOnlyQuestion, viewBodyFromIz } from '../../question-bank/viewOnly.ts';
import { i18n } from '../../i18n/index.ts';
import { QUIZ_IMAGE_CLOSE, QUIZ_IMAGE_OPEN, type QuizBody, type QuizQuestion } from './body.ts';
import { htmlToPlainText, quizMetaLine, quizQuestionFromView, quizQuestionsFromViewOnly } from './fromBank.ts';

export function unsupportedQuestionHint(): string {
  return i18n.t('quiz:hint.unsupported');
}

const GROUP_TYPES = ['group', 'direct-fill-blank-group'];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

/** The exam a test unit wraps: a bare id, or a populated exam document. */
export function examIdOf(unit: object | null | undefined): string | null {
  const exam = unit ? (unit as { exam?: unknown }).exam : null;
  if (typeof exam === 'string') return exam.trim() || null;
  if (isRecord(exam)) {
    const id = exam.id || exam._id;
    return typeof id === 'string' && id ? id : null;
  }
  return null;
}

const IMG_TAG = /<img\b[^>]*>/gi;
const SRC_ATTR = /\bsrc\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i;
const DATA_IMAGE = /^data:image\/(png|jpe?g|gif|webp|svg\+xml);base64,/i;

/** A drawable image URL, or null for anything that is not https or an inline raster. */
export function imageSource(src: string): string | null {
  const raw = src.trim().replace(/&amp;/g, '&');
  if (DATA_IMAGE.test(raw)) return raw;
  if (!/^https?:\/\//i.test(raw)) return null;
  return convertURL(raw);
}

/** Every `<img>` in `html` as an image token indexing `images`; an unusable src drops the tag. */
export function tokenizeImages(html: string, images: string[]): string {
  if (!html || !/<img\b/i.test(html)) return html;
  return html.replace(IMG_TAG, (tag) => {
    const match = SRC_ATTR.exec(tag);
    const url = match ? imageSource(match[1] || match[2] || match[3] || '') : null;
    if (!url) return ' ';
    let index = images.indexOf(url);
    if (index === -1) index = images.push(url) - 1;
    // spaced, so the painter's word wrap treats an image as a word of its own
    return ' ' + QUIZ_IMAGE_OPEN + index + QUIZ_IMAGE_CLOSE + ' ';
  });
}

function tokenizeRow(raw: IzQuestion, images: string[]): IzQuestion {
  const next: IzQuestion = { ...raw, html: tokenizeImages(typeof raw.html === 'string' ? raw.html : '', images) };
  if (typeof raw.correct === 'string') next.correct = tokenizeImages(raw.correct, images);
  const answers = raw.answers;
  if (Array.isArray(answers)) {
    next.answers = answers.map((answer) => (isRecord(answer) ? { ...answer, value: tokenizeImages(String(answer.value || ''), images) } : answer));
  } else if (isRecord(answers)) {
    const map: Record<string, typeof answers[string]> = {};
    Object.keys(answers).forEach((key) => {
      const answer = answers[key];
      map[key] = isRecord(answer) ? { ...answer, value: tokenizeImages(String(answer.value || ''), images) } : answer;
    });
    next.answers = map;
  }
  return next;
}

/** Top-level rows in exam order. `*-child` rows belong to a group, which fetches its own by id. */
export function examTopLevelRows(rows: IzQuestion[]): IzQuestion[] {
  return rows
    .map((raw, position) => ({ raw, position }))
    .filter((entry) => typeof entry.raw.type === 'string' && !/-child$/.test(entry.raw.type))
    .sort((a, b) => {
      const ia = Number((a.raw as { index?: unknown }).index);
      const ib = Number((b.raw as { index?: unknown }).index);
      const sa = Number.isFinite(ia) ? ia : Number.MAX_SAFE_INTEGER;
      const sb = Number.isFinite(ib) ? ib : Number.MAX_SAFE_INTEGER;
      return sa - sb || a.position - b.position;
    })
    .map((entry) => entry.raw);
}

/** A type the board has no model for: its stem, and a note saying so. */
function unsupportedQuestion(raw: IzQuestion): QuizQuestion {
  const stem = htmlToPlainText(typeof raw.html === 'string' ? raw.html : '');
  return { prompt: stem || i18n.t('quiz:text.noPreview'), options: [], correct: -1, hint: unsupportedQuestionHint() };
}

function rowQuestions(raw: IzQuestion, children: IzQuestion[] | null): QuizQuestion[] {
  if (GROUP_TYPES.indexOf(raw.type) >= 0) {
    const view = toViewOnlyQuestion(raw);
    if (view && view.kind !== 'other') {
      const attached = attachGroupChildren([view], children)[0];
      if (attached.kind !== 'other') return quizQuestionsFromViewOnly(attached);
    }
    return [unsupportedQuestion(raw)];
  }
  const body = viewBodyFromIz(raw);
  return body ? [quizQuestionFromView(body)] : [unsupportedQuestion(raw)];
}

function tokenIndexes(text: string | undefined): number[] {
  if (!text) return [];
  const out: number[] = [];
  const pattern = new RegExp(QUIZ_IMAGE_OPEN + '(\\d+)' + QUIZ_IMAGE_CLOSE, 'g');
  let match = pattern.exec(text);
  while (match) {
    out.push(Number(match[1]));
    match = pattern.exec(text);
  }
  return out;
}

/** A raster figure (not an inline SVG formula) needs a page to itself to be readable. */
function hasFigure(question: QuizQuestion, images: string[]): boolean {
  const texts = [question.prompt, question.stem].concat(question.options);
  return texts.some((text) => tokenIndexes(text).some((index) => {
    const url = images[index] || '';
    return !/\.svg(\?|$)/i.test(url) && !/^data:image\/svg/i.test(url);
  }));
}

/**
 * Exam rows as a quiz body. `children` are the group children fetched by id (null when that failed).
 * Rows from the public GET carry no `correct`, so the set then simply has nothing to reveal.
 */
export function examQuizBody(title: string, rows: IzQuestion[], children: IzQuestion[] | null): QuizBody {
  const images: string[] = [];
  const kids = children ? children.map((raw) => tokenizeRow(raw, images)) : null;
  const top = examTopLevelRows(rows);
  const questions: QuizQuestion[] = [];
  top.forEach((raw, index) => {
    rowQuestions(tokenizeRow(raw, images), kids).forEach((question) => {
      question.number = index + 1;
      if (!question.solo && hasFigure(question, images)) question.solo = true;
      questions.push(question);
    });
  });
  const body: QuizBody = { title, meta: quizMetaLine(i18n.t('quiz:source.test'), top.length), questions };
  if (images.length > 0) body.images = images;
  return body;
}

/** True when the reveal has anything to show: a key, a filled blank or a Đúng / Sai mark. */
export function quizHasAnswers(body: QuizBody): boolean {
  return body.questions.some((question) => question.correct >= 0
    || Boolean(question.correctIndexes && question.correctIndexes.length > 0)
    || Boolean(question.revealPrompt)
    || Boolean(question.statementKeys && question.statementKeys.some((key) => typeof key === 'boolean')));
}

/** Group child ids the top-level rows point at, once each. */
export function examChildIds(rows: IzQuestion[]): string[] {
  const out: string[] = [];
  examTopLevelRows(rows).forEach((raw) => {
    if (GROUP_TYPES.indexOf(raw.type) === -1 || !Array.isArray(raw.questions)) return;
    raw.questions.forEach((entry: unknown) => {
      const id = typeof entry === 'string' ? entry : isRecord(entry) ? String(entry.id || entry._id || '') : '';
      if (id && out.indexOf(id) === -1) out.push(id);
    });
  });
  return out;
}

/** A random-question exam: every attempt draws its own questions, so there is no fixed set to paint. */
export interface RandomExamSummary {
  count: number;
  topics: string[];
}

/** The draw plan of a `random-question-test` exam, or null for an exam with a fixed question set. */
export function randomExamSummary(exam: unknown): RandomExamSummary | null {
  if (!isRecord(exam) || exam.type !== 'random-question-test') return null;
  const rows = Array.isArray(exam.matrix) ? exam.matrix : [];
  let count = 0;
  const topics: string[] = [];
  rows.forEach((row: unknown) => {
    if (!isRecord(row)) return;
    const quantity = Number(row.quantity);
    if (Number.isFinite(quantity) && quantity > 0) count += quantity;
    const topic = typeof row['knowledge-title'] === 'string' ? row['knowledge-title'].trim() : '';
    if (topic && topics.indexOf(topic) === -1) topics.push(topic);
  });
  return { count, topics };
}
